// LandingFiltersPanel.jsx (Client Component)
'use client';

import { useMemo, useState } from 'react';
import { useTranslationDetector } from '@/hooks/useTranslationDetector';
import { byPrefixAndName } from '@awesome.me/kit-12cf79935c/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from '@/components/ui/input/Checkbox';
import Radio from '@/components/ui/input/Radio';
import Badge from '@/components/ui/Badge';
import clsx from 'clsx';
import {
  useHotelLandingFilterDataLayer,
  FILTER_CATEGORY_MAP,
} from '@/components/pages/hotel/search/hooks/useHotelLandingFilterDataLayer';

// Amenity code to icon mapping
const amenityIconMap = {
  3: "circle-wifi",
  7: "pet-custom",
  8: "wheelchair-1",
  9: "circle-parking",
  11: "vault",
  13: "water-ladder-1-1",
  22: "child-hotel",
  23: "air-conditioner",
  27: "bus-service-1",
  28: "umbrella-beach-1",
  30: "hot-tub-person",
  39: "water-ladder-1-1",
  60: "water-ladder-1-1",
  85: "unlock-room",
  88: "cstm-smarttv",
  89: "custom-nespresso",
  107: "hotel-building-new",
  159: "person-swimming-child-1",
  186: "charging-station-1",
  247: "water",
  257: "spa-1",
  267: "shower",
  268: "shower",
  269: "bath",
  270: "hair-dryer",
  272: "mini-bar",
  292: "terrace-new",
  295: "leak-sea",
  296: "leak-sea",
  297: "tree",
  302: "airplane-otel",
  372: "mountbig",
  373: "blueflag",
  404: "tulip",
  563: "umbrella-beach-1",
  720: "circle-parking",
  721: "circle-parking",
  727: "circle-parking"
};

/**
 * Hotel landing page filtre paneli.
 *
 * @param {Object} props
 * @param {Object} props.filters - Mevcut filtre değerleri
 * @param {Function} props.onFilterChange - Filtre güncellendiğinde çağrılır
 * @param {Object} props.filterOptions - Response'tan gelen dinamik filtre seçenekleri
 * @param {Array} props.filterOptions.locations - İlçe/Bölge listesi
 * @param {Array} props.filterOptions.themes - Temalar
 * @param {Array} props.filterOptions.amenities - Tesis özellikleri
 */
export default function LandingFiltersPanel({
  filters = {},
  onFilterChange = () => { },
  filterOptions = {},
  currentSortId = '0',
}) {
  const t = useTranslationDetector('hotel');
  const {
    stars = [],
    chains = [],
    locations = [],
    themes = [],
    amenities = [],
    rating = '',
  } = filters;

  const { sendFilterAppliedEvent, getSortingType } =
    useHotelLandingFilterDataLayer();
  const sortingType = getSortingType(currentSortId);

  // "Daha fazla göster" state'leri
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [showAllChains, setShowAllChains] = useState(false);
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  // Aktif filtreleri hesapla
  const activeFilters = useMemo(() => {
    const active = [];

    // Yıldız filtreleri
    if (stars && stars.length > 0) {
      stars.forEach((star) => {
        active.push({
          type: 'star',
          value: star,
          label: t('starsFilter', { star }),
          key: `star-${star}`,
        });
      });
    }

    // İlçe/Bölge filtreleri
    if (locations && locations.length > 0) {
      locations.forEach((locationId) => {
        const location = filterOptions.locations?.find(l => l.Id === locationId);
        if (location) {
          active.push({
            type: 'location',
            value: locationId,
            label: location.Name,
            key: `location-${locationId}`,
          });
        }
      });
    }

    // Tema filtreleri
    if (themes && themes.length > 0) {
      themes.forEach((themeId) => {
        const theme = filterOptions.themes?.find(t => t.Id === themeId);
        if (theme) {
          active.push({
            type: 'theme',
            value: themeId,
            label: theme.Name,
            key: `theme-${themeId}`,
          });
        }
      });
    }

    // Zincir filtreleri
    if (chains && chains.length > 0) {
      chains.forEach((chainSlug) => {
        const chain = filterOptions.chains?.find(c => c.Slug === chainSlug);
        active.push({
          type: 'chain',
          value: chainSlug,
          label: chain?.Name || chainSlug,
          key: `chain-${chainSlug}`,
        });
      });
    }

    // Tesis özellikleri filtreleri
    if (amenities && amenities.length > 0) {
      amenities.forEach((amenityId) => {
        const amenity = filterOptions.amenities?.find(a => a.Id === amenityId);
        if (amenity) {
          active.push({
            type: 'amenity',
            value: amenityId,
            label: amenity.Name,
            key: `amenity-${amenityId}`,
          });
        }
      });
    }

    // Değerlendirme puanı
    if (rating) {
      const ratingLabels = {
        '9+': `${t('Excellent')} 9+`,
        '8+': `${t('Great')} 8+`,
        '7+': `${t('VeryGood')} 7+`,
        '6+': `${t('Good')} 6+`,
      };
      active.push({
        type: 'rating',
        value: rating,
        label: ratingLabels[rating] || rating,
        key: 'rating',
      });
    }

    return active;
  }, [stars, chains, locations, themes, amenities, rating, filterOptions, t]);

  const activeFiltersCount = activeFilters.length;

  // Filtre kaldırma fonksiyonu
  const removeFilter = (filterToRemove) => {
    const updatedFilters = { ...filters };

    switch (filterToRemove.type) {
      case 'star':
        updatedFilters.stars = (updatedFilters.stars || []).filter(
          (s) => s !== filterToRemove.value
        );
        break;
      case 'location':
        updatedFilters.locations = (updatedFilters.locations || []).filter(
          (l) => l !== filterToRemove.value
        );
        break;
      case 'theme':
        updatedFilters.themes = (updatedFilters.themes || []).filter(
          (t) => t !== filterToRemove.value
        );
        break;
      case 'chain':
        updatedFilters.chains = (updatedFilters.chains || []).filter(
          (c) => c !== filterToRemove.value
        );
        break;
      case 'amenity':
        updatedFilters.amenities = (updatedFilters.amenities || []).filter(
          (a) => a !== filterToRemove.value
        );
        break;
      case 'rating':
        updatedFilters.rating = '';
        break;
    }

    onFilterChange(updatedFilters);
  };

  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    const clearedFilters = {
      stars: [],
      chains: [],
      locations: [],
      themes: [],
      amenities: [],
      rating: '',
    };
    onFilterChange(clearedFilters);
  };

  // Yıldız sayısı değişikliği
  const handleStarChange = (starValue, checked) => {
    let newStars = [...(stars || [])];
    if (checked) {
      if (!newStars.includes(starValue)) {
        newStars.push(starValue);
        const subcategory =
          starValue === 0 ? t('unrated') : t('starsFilter', { star: starValue });
        sendFilterAppliedEvent(
          FILTER_CATEGORY_MAP.star,
          subcategory,
          sortingType
        );
      }
    } else {
      newStars = newStars.filter((s) => s !== starValue);
    }
    const updatedFilters = { ...filters, stars: newStars };
    onFilterChange(updatedFilters);
  };

  // İlçe/Bölge değişikliği
  const handleLocationChange = (locationId, checked) => {
    let newLocations = [...(locations || [])];
    if (checked) {
      if (!newLocations.includes(locationId)) {
        newLocations.push(locationId);
        const location = filterOptions.locations?.find((l) => l.Id === locationId);
        const subcategory = location?.Name ?? String(locationId);
        sendFilterAppliedEvent(
          FILTER_CATEGORY_MAP.location,
          subcategory,
          sortingType
        );
      }
    } else {
      newLocations = newLocations.filter((l) => l !== locationId);
    }
    const updatedFilters = { ...filters, locations: newLocations };
    onFilterChange(updatedFilters);
  };

  // Tema değişikliği
  const handleThemeChange = (themeId, checked) => {
    let newThemes = [...(themes || [])];
    if (checked) {
      if (!newThemes.includes(themeId)) {
        newThemes.push(themeId);
        const theme = filterOptions.themes?.find((t) => t.Id === themeId);
        const subcategory = theme?.Name ?? String(themeId);
        sendFilterAppliedEvent(
          FILTER_CATEGORY_MAP.theme,
          subcategory,
          sortingType
        );
      }
    } else {
      newThemes = newThemes.filter((t) => t !== themeId);
    }
    const updatedFilters = { ...filters, themes: newThemes };
    onFilterChange(updatedFilters);
  };

  // Zincir değişikliği
  const handleChainChange = (chainSlug, checked) => {
    let newChains = [...(chains || [])];
    if (checked) {
      if (!newChains.includes(chainSlug)) {
        newChains.push(chainSlug);
        const chain = filterOptions.chains?.find((c) => c.Slug === chainSlug);
        const subcategory = chain?.Name ?? String(chainSlug);
        sendFilterAppliedEvent(
          FILTER_CATEGORY_MAP.chain,
          subcategory,
          sortingType
        );
      }
    } else {
      newChains = newChains.filter((c) => c !== chainSlug);
    }
    const updatedFilters = { ...filters, chains: newChains };
    onFilterChange(updatedFilters);
  };

  // Tesis özelliği değişikliği
  const handleAmenityChange = (amenityId, checked) => {
    let newAmenities = [...(amenities || [])];
    if (checked) {
      if (!newAmenities.includes(amenityId)) {
        newAmenities.push(amenityId);
        const amenity = filterOptions.amenities?.find((a) => a.Id === amenityId);
        const subcategory = amenity?.Name ?? String(amenityId);
        sendFilterAppliedEvent(
          FILTER_CATEGORY_MAP.amenity,
          subcategory,
          sortingType
        );
      }
    } else {
      newAmenities = newAmenities.filter((a) => a !== amenityId);
    }
    const updatedFilters = { ...filters, amenities: newAmenities };
    onFilterChange(updatedFilters);
  };

  // Değerlendirme puanı değişikliği
  const handleRatingChange = (ratingValue) => {
    const subcategory =
      ratingValue === "unrated" ? t('unrated') : ratingValue;
    if (ratingValue) {
      sendFilterAppliedEvent(
        FILTER_CATEGORY_MAP.rating,
        subcategory,
        sortingType
      );
    }
    const updatedFilters = { ...filters, rating: ratingValue };
    onFilterChange(updatedFilters);
  };

  // Statik seçenekler
  const starOptions = [5, 4, 3, 2, 1, 0]; // 0 = Derecelendirilmemiş
  const ratingOptions = [
    { value: '9+', label: t('Excellent'), labelPoint: '9+' },
    { value: '8+', label: t('Great'), labelPoint: '8+' },
    { value: '7+', label: t('VeryGood'), labelPoint: '7+' },
    { value: '6+', label: t('Good'), labelPoint: '6+' },
  ];

  return (
    <aside className="rounded-xl bg-white p-4 shadow-sm">
      {/* Aktif Filtreler Bölümü */}
      {activeFiltersCount > 0 && (
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">
              {t('filtersSelected', { count: activeFiltersCount })}
            </p>
            <button
              onClick={clearAllFilters}
              className="text-sm font-medium text-primary hover:text-primary-emphasis transition-colors"
            >
              {t('clear')}
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {activeFilters.map((filter) => (
              <Badge
                key={filter.key}
                variant="secondary"
                size="sm"
                className="group flex items-center gap-2 cursor-pointer py-2 pl-[10px] pr-2"
              >
                <span className='text-sm font-normal text-primary'>{filter.label}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter(filter);
                  }}
                  className="ml-1 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
                  aria-label={t('removeFilter')}
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far['xmark']}
                    className="h-4 w-4 text-primary cursor-pointer"
                  />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {/* İlçe / Bölge */}
        {filterOptions.locations && filterOptions.locations.length > 0 && (
          <CollapsibleFilterSection title={t('districtRegion')} defaultOpen={true}>
            <div className="flex flex-col gap-[14px]">
              {(showAllLocations ? filterOptions.locations : filterOptions.locations.slice(0, 5)).map((location) => (
                <Checkbox
                  key={location.Id}
                  checked={locations?.includes(location.Id) || false}
                  onChange={(e) => handleLocationChange(location.Id, e.target.checked)}
                >
                  <span className="text-sm font-normal text-foreground">{location.Name}</span>
                </Checkbox>
              ))}
              {filterOptions.locations.length > 5 && (
                <button
                  onClick={() => setShowAllLocations(!showAllLocations)}
                  className="text-sm font-medium text-primary text-left cursor-pointer"
                >
                  {showAllLocations ? t('showLess') : t('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}

        {/* Misafir Puanı (Rating) */}
        <CollapsibleFilterSection title={t('guestRating')} defaultOpen={true}>
          <div className="flex flex-col gap-[14px]">
            {ratingOptions.map((ratingOption) => (
              <Radio
                key={ratingOption.value}
                name="rating"
                checked={rating === ratingOption.value}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleRatingChange(ratingOption.value);
                  }
                }}
              >
                <div className="flex items-center gap-1">
                  <span className="text-sm font-normal text-foreground font-normal">{ratingOption.label}</span>
                  <span className="text-sm text-foreground font-medium">{ratingOption.labelPoint}</span>
                </div>
              </Radio>
            ))}
            <Radio
              name="rating"
              checked={rating === 'unrated'}
              onChange={(e) => {
                if (e.target.checked) {
                  handleRatingChange('unrated');
                }
              }}
            >
              <span className="text-sm font-normal text-foreground">{t('unrated')}</span>
            </Radio>
          </div>
        </CollapsibleFilterSection>

        {/* Yıldız Sayısı */}
        <CollapsibleFilterSection title={t('hotelStars')} defaultOpen={true}>
          <div className="flex flex-col gap-[14px]">
            {starOptions.map((star) => (
              <Checkbox
                key={star}
                checked={stars?.includes(star) || false}
                onChange={(e) => handleStarChange(star, e.target.checked)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-normal text-foreground">
                    {star === 0 ? t('unrated') : t('starsFilter', { star })}
                  </span>
                  {star > 0 && (
                    <div className="flex items-center gap-[1px]">
                      {Array.from({ length: star }).map((_, index) => (
                        <span key={index} className='!w-3.5 !h-3.5 flex items-center justify-center'>
                          <FontAwesomeIcon
                            key={index}
                            icon={byPrefixAndName.fas['star']}
                            className="text-yellow !w-[12px] !h-[11px]"
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Checkbox>
            ))}
          </div>
        </CollapsibleFilterSection>

        {/* Zincirler */}
        {filterOptions.chains && filterOptions.chains.length > 0 && (
          <CollapsibleFilterSection title={t('chains')} defaultOpen={true}>
            <div className="flex flex-col gap-[14px]">
              {(showAllChains ? filterOptions.chains : filterOptions.chains.slice(0, 5)).map((chain) => (
                <Checkbox
                  key={chain.Slug}
                  checked={chains?.includes(chain.Slug) || false}
                  onChange={(e) => handleChainChange(chain.Slug, e.target.checked)}
                >
                  <span className="text-sm font-normal text-foreground">{chain.Name}</span>
                </Checkbox>
              ))}
              {filterOptions.chains.length > 5 && (
                <button
                  onClick={() => setShowAllChains(!showAllChains)}
                  className="text-sm font-medium text-primary text-left cursor-pointer"
                >
                  {showAllChains ? t('showLess') : t('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}

        {/* Temalar */}
        {filterOptions.themes && filterOptions.themes.length > 0 && (
          <CollapsibleFilterSection title={t('themes')} defaultOpen={true}>
            <div className="flex flex-col gap-[14px]">
              {(showAllThemes ? filterOptions.themes : filterOptions.themes.slice(0, 5)).map((theme) => (
                <Checkbox
                  key={theme.Id}
                  checked={themes?.includes(theme.Id) || false}
                  onChange={(e) => handleThemeChange(theme.Id, e.target.checked)}
                >
                  <span className="text-sm font-normal text-foreground">{theme.Name}</span>
                </Checkbox>
              ))}
              {filterOptions.themes.length > 5 && (
                <button
                  onClick={() => setShowAllThemes(!showAllThemes)}
                  className="text-sm font-medium text-primary text-left cursor-pointer"
                >
                  {showAllThemes ? t('showLess') : t('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}

        {/* Tesis Özellikleri */}
        {filterOptions.amenities && filterOptions.amenities.length > 0 && (
          <CollapsibleFilterSection title={t('facilityFeatures')} defaultOpen={true}>
            <div className="grid grid-cols-2 gap-x-[14px] gap-y-4">
              {(showAllAmenities ? filterOptions.amenities : filterOptions.amenities.slice(0, 8)).map((amenity) => {
                const isChecked = amenities?.includes(amenity.Id) || false;
                // Amenity Code'u bul (Code, AmenityId veya Id alanından)
                const amenityCode = amenity.Code || amenity.AmenityId || amenity.Id;
                const iconName = amenityIconMap[amenityCode] || "home";
                // Öncelik sırası: fak (custom) -> fas (FontAwesome solid) -> fallback home
                const icon = byPrefixAndName.fak[iconName] || byPrefixAndName.fal[iconName] || byPrefixAndName.fal["home"];

                return (
                  <button
                    key={amenity.Id}
                    type="button"
                    onClick={() => handleAmenityChange(amenity.Id, !isChecked)}
                    className={clsx(
                      "flex flex-col items-center justify-center gap-[6px] px-3 py-[10px] rounded-lg border border-border transition-all duration-200 cursor-pointer",
                      "hover:border-primary hover:bg-primary-weaker",
                      isChecked
                        ? "border-primary bg-primary-weaker"
                        : "border-border-strong bg-white"
                    )}
                  >
                    <FontAwesomeIcon
                      icon={icon}
                      className={clsx(
                        "text-xl w-6! h-6!",
                        isChecked ? "text-primary" : "text-foreground"
                      )}
                    />
                    <span className={clsx(
                      "text-sm font-medium text-center",
                      isChecked ? "text-primary" : "text-foreground"
                    )}>
                      {amenity.Name}
                    </span>
                  </button>
                );
              })}
              {filterOptions.amenities.length > 8 && (
                <button
                  onClick={() => setShowAllAmenities(!showAllAmenities)}
                  className="text-sm font-medium text-primary text-left cursor-pointer col-span-2"
                >
                  {showAllAmenities ? t('showLess') : t('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}
      </div>
    </aside>
  );
}

/**
 * Collapsible filtre bölümü component'i
 */
function CollapsibleFilterSection({ title, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0 flex flex-col gap-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between text-left cursor-pointer"
      >
        <p className="text-base text-foreground font-semibold">{title}</p>
        <FontAwesomeIcon
          icon={byPrefixAndName.far['chevron-down']}
          className={clsx(
            'h-5 w-5 text-foreground transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && <div className="flex flex-col gap-4">{children}</div>}
    </div>
  );
}

